import type { PaginatedLocalGiftCardDto } from "common/models/api/models/PaginatedLocalGiftCardDto";
import type { QueryKey } from "react-query";
import { useMutation, UseMutationResult, useQueryClient } from "react-query";
import type { UseCustomMutationOptions } from "ui/query/react-query-overrides";
import { Queries } from "ui/query/queries";
import { giftcardsApi } from "../api/giftcardsApi";

type Variables = { id: string; is_active: boolean };
type PreviousCache = Array<[QueryKey, PaginatedLocalGiftCardDto | undefined]>;

function applyOptimisticUpdate(
    previous: PaginatedLocalGiftCardDto | undefined,
    id: string,
    is_active: boolean,
): PaginatedLocalGiftCardDto | undefined {
    if (!previous?.items) return previous;
    return {
        ...previous,
        items: previous.items.map((item) => (item.id.toString() === id ? { ...item, isActive: is_active } : item)),
    };
}

export default function useUpdateGiftCardStatus({
    onSuccess,
    onError,
    ...options
}: UseCustomMutationOptions<void, unknown, Variables>): UseMutationResult<void, unknown, Variables> {
    const queryClient = useQueryClient();

    const handleOnSuccess = (data: void, variables: Variables, context: unknown) => {
        queryClient.invalidateQueries(Queries.GIFTCARDS_FIND_ALL);
        onSuccess?.(data, variables, context);
    };

    const handleOnMutate = async (variables: Variables): Promise<PreviousCache> => {
        await queryClient.cancelQueries(Queries.GIFTCARDS_FIND_ALL);
        const previousCache = queryClient.getQueriesData<PaginatedLocalGiftCardDto>(Queries.GIFTCARDS_FIND_ALL);
        previousCache.forEach(([queryKey, data]) => {
            if (data) {
                queryClient.setQueryData<PaginatedLocalGiftCardDto>(
                    queryKey,
                    applyOptimisticUpdate(data, variables.id, variables.is_active) ?? data,
                );
            }
        });
        return previousCache;
    };

    const handleOnError = (err: unknown, variables: Variables, context: unknown) => {
        if (Array.isArray(context)) {
            context.forEach(([queryKey, data]) => queryClient.setQueryData(queryKey, data));
        }
        onError?.(err, variables, context);
    };

    return useMutation(
        async ({ id, is_active }: Variables) => {
            await giftcardsApi.updateLocalGiftCardActive(id, { is_active });
        },
        {
            onMutate: handleOnMutate,
            onSuccess: handleOnSuccess,
            onError: handleOnError,
            ...options,
        },
    );
}
